import { z } from "zod"
import type { FaqItem, LegalDocument, Notice } from "../content/content-types.ts"
import { blockedLegalDocument, parsePublicLegalDocument } from "./legal-markdown.ts"

const noticeSchema = z.strictObject({
  category: z.enum(["service", "security"]),
  id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  pinned: z.boolean(),
  publishedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  sections: z.array(z.strictObject({ body: z.string(), title: z.string() })),
  summary: z.string(),
  title: z.string(),
  version: z.string(),
})
const faqSchema = z.strictObject({ answer: z.string(), category: z.enum(["account", "balance", "exchange", "transfer"]), id: z.string(), question: z.string() })
const noticeListSchema = z.strictObject({ items: z.array(noticeSchema) })
const faqListSchema = z.strictObject({ items: z.array(faqSchema), version: z.string() })

export type PublicNoticeCollection = { readonly items: readonly Notice[]; readonly state: "ready" | "unavailable" }
export type PublicFaqCollection = { readonly items: readonly FaqItem[]; readonly state: "ready" | "unavailable"; readonly version: string }
export type PublicNoticeResult = { readonly item: Notice | null; readonly state: "missing" | "ready" | "unavailable" }
export type PublicLegalResult = { readonly document: LegalDocument; readonly state: "missing" | "ready" | "unavailable" }

const apiBase = (): string | null => {
  const value = process.env["CASHHUB_API_INTERNAL_BASE_URL"] ?? process.env["NEXT_PUBLIC_API_BASE_URL"]
  return value !== undefined && /^https?:\/\//.test(value) ? value.replace(/\/$/, "") : null
}

const read = async (path: string): Promise<Response | null> => {
  const base = apiBase()
  if (base === null) return null
  try {
    return await fetch(`${base}${path}`, { cache: "no-store", headers: { accept: "application/json" }, signal: AbortSignal.timeout(5_000) })
  } catch (error: unknown) {
    if (error instanceof Error) return null
    throw error
  }
}

export const loadPublicNotices = async (): Promise<PublicNoticeCollection> => {
  const response = await read("/public/content/notices")
  if (response === null || !response.ok) return { items: [], state: "unavailable" }
  const parsed = noticeListSchema.safeParse(await response.json())
  return parsed.success ? { items: parsed.data.items, state: "ready" } : { items: [], state: "unavailable" }
}

export const loadPublicFaqs = async (): Promise<PublicFaqCollection> => {
  const response = await read("/public/content/faqs")
  if (response === null || !response.ok) return { items: [], state: "unavailable", version: "확인 중" }
  const parsed = faqListSchema.safeParse(await response.json())
  return parsed.success ? { items: parsed.data.items, state: "ready", version: parsed.data.version } : { items: [], state: "unavailable", version: "확인 중" }
}

export const loadPublicNotice = async (id: string): Promise<PublicNoticeResult> => {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id)) return { item: null, state: "missing" }
  const response = await read(`/public/content/notices/${encodeURIComponent(id)}`)
  if (response === null) return { item: null, state: "unavailable" }
  if (response.status === 404) return { item: null, state: "missing" }
  if (!response.ok) return { item: null, state: "unavailable" }
  const parsed = noticeSchema.safeParse(await response.json())
  return parsed.success ? { item: parsed.data, state: "ready" } : { item: null, state: "unavailable" }
}

const legalTitle = (slug: "terms-point" | "terms-privacy" | "terms-service"): string => {
  if (slug === "terms-service") return "CashHub 서비스 이용약관"
  if (slug === "terms-privacy") return "CashHub 개인정보처리방침"
  return "CashHub 전환·통합잔액 운영정책"
}

export const loadPublicLegalDocument = async (slug: "terms-point" | "terms-privacy" | "terms-service"): Promise<PublicLegalResult> => {
  const blocked = blockedLegalDocument(slug, legalTitle(slug))
  const response = await read(`/public/content/terms/${slug}`)
  if (response === null) return { document: blocked, state: "unavailable" }
  if (response.status === 404) return { document: blocked, state: "missing" }
  if (!response.ok) return { document: blocked, state: "unavailable" }
  try {
    return { document: parsePublicLegalDocument(await response.json()), state: "ready" }
  } catch (error: unknown) {
    if (error instanceof Error) return { document: blocked, state: "unavailable" }
    throw error
  }
}
